<template>
  <ModalTooManyFails
    v-if="result === 'VerifyResult.TooManyFails'"
    @close="onModalCloseTooManyFails"
    :openOnInit="result === 'VerifyResult.TooManyFails'"
  />
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto h-[100px] w-auto" src="/img/spike-logo.svg" alt="Spike" />
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">Check your email for a code</h2>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10"
        :class="result === 'VerifyResult.Fail' ? 'shakeX' : ''"
      >
        <div class="space-y-6 text-gray-700 text-sm">
          <p>
            We've sent a 6-digit code to <b>{{ email }}</b
            >.
          </p>
          <p> Please check your email and enter the code here. </p>

          <div>
            <div class="mt-1">
              <input
                v-model="pin"
                name="pin"
                type="text"
                autocomplete="off"
                required
                oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                :maxlength="requiredPinLength"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              class="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-600 disabled:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
              :disabled="
                result === 'loading' ||
                result === 'VerifyResult.Success' ||
                pin === null ||
                pin.length < requiredPinLength
              "
              @click="onVerify()"
            >
              <div v-if="result === 'loading'">Verifying ...</div>
              <div v-else-if="result === 'VerifyResult.Success'">Verified</div>
              <div v-else>Verify</div>
            </button>
          </div>
        </div>
        <div class="mt-6 flex w-full justify-center text-sm">
          <template v-if="result === 'loading'">
            <ScalingDots />
          </template>
          <template v-else-if="result === 'exception'">
            <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
            <span class="ml-2 text-red-600">failed to contact the server, please retry</span>
          </template>
          <template v-else-if="result === 'VerifyResult.Fail'">
            <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
            <span class="ml-2 text-red-600">pin incorrect {{ attempts }}</span>
            <!-- see warning below -->
          </template>
          <template v-else-if="result === 'VerifyResult.Success'">
            <CheckCircleIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
            <span class="ml-2 text-green-600">success</span>
          </template>
          <template v-else>
            <!-- dummy - keep height -->
            <span>&nbsp;</span>
          </template>
        </div>
        <div v-if="result === 'VerifyResult.Fail'" class="mt-6 flex w-full justify-center text-sm">
          <span class="ml-2 text-red-600">warning: your account will be locked after 5 fails</span>
        </div>
      </div>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md text-center text-gray-500">
      Can’t find our code? check your spam folder.
    </div>
  </div>
</template>

<script setup lang="ts">
import { Url, VerifyRes, VerifyResult, VerifyReq, ScreenName } from "@spike/app2-sitemap/src/auth.js";
import axios from "axios";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/solid";
import sleep from "@util/sleep";
import { onMounted, ref } from "vue";
import ScalingDots from "../../components/spinners/ScalingDots.vue";
import ModalTooManyFails from "../../components/VerifyLocalEmail/ModalTooManyFails.vue";
import * as Nav from "../../navigation";
import { Screen } from "../../routes";
import * as Store from "../../store";

const email = ref(Store.forgotEmail);
const requiredPinLength = ref<number>(6);
const pin = ref<string | null>(null);
const result = ref<string | null>(null);
const attempts = ref<string | null>(null);

onMounted(async () => {
  // console.log("onMounted:", ScreenRoute.ForgotPasswordVerify);
  Nav.prevScreensComplete(ScreenName.ForgotPasswordVerify);
});

async function onVerify() {
  try {
    result.value = "loading";
    const requestBody: VerifyReq = {
      pin: pin.value as string,
      email: email.value as string,
    };

    const response = await axios.post(Url.server.shared.forgotPassword.verify, requestBody);
    // console.log("response:", response);
    const res: VerifyRes = response.data;

    switch (res.result) {
      case VerifyResult.Success:
        onVerifySuccess();
        break;
      case VerifyResult.Fail:
        onVerifyFail(res);
        break;
      case VerifyResult.TooManyFails:
        onVerifyTooManyFails(res);
        break;
      default:
        onVerifyException();
        break;
    }
  } catch (e) {
    // console.error("sign-in failed:", e);
    onVerifyException();
  }
}

async function onVerifyTest() {
  result.value = "loading";
  await sleep(500);

  // testing
  onVerifySuccess();
  // onVerifyFail();
  // onVerifyTooManyFails();
  // onVerifyException();
}

async function onVerifyException() {
  result.value = "exception";
}

async function onVerifyFail(res: VerifyRes) {
  result.value = "VerifyResult.Fail";
  if (res.confirmAttempts) {
    attempts.value = res.confirmAttempts + "/" + res.max;
  }
}

async function onVerifyTooManyFails(res: VerifyRes) {
  result.value = "VerifyResult.TooManyFails";
  attempts.value = res.confirmAttempts + "/" + res.max;
  // see onModalCloseTooManyFails()
}

async function onVerifySuccess() {
  // show message
  result.value = "VerifyResult.Success";

  // wait 1 second the go to next
  await sleep(1000);
  Nav.complete(Screen.ForgotPasswordVerify);
  Nav.setActive(Screen.ForgotPasswordSetPassword, true);
}

function onModalCloseTooManyFails() {
  location.href = "/";
}
</script>
